import { boxCenter, pt, type Box, type Pt } from '../model/geom';
import type { Micron } from '../model/units';

/**
 * 뷰포트 — 월드(µm) ↔ 화면(px) 변환 (작업지시서 P2-02)
 *
 * CAD 관례를 따른다:
 *   · 월드 Y축은 위쪽이 +  (화면 Y축은 아래쪽이 + 이므로 부호를 뒤집는다)
 *   · 확대/축소는 **커서 아래 점이 고정**되도록 한다 (AutoCAD 휠 줌과 동일)
 *
 * scale 단위: px per µm.
 *   예) scale = 0.0001 이면 1,000,000µm(=1m)가 100px.
 */

/** 확대 한계. 너무 확대하면 정수 좌표가 화면에서 계단처럼 보인다. */
const MIN_SCALE = 1e-7; // 1m ≈ 0.1px (아주 축소)
const MAX_SCALE = 0.02; // 1mm = 20px (아주 확대)

export class Viewport {
  /** 화면 중심에 오는 월드 좌표 */
  center: Pt = pt(0, 0);
  /** px / µm */
  scale = 0.0002;

  width = 800;
  height = 600;

  resize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
  }

  /* --------------------------------------------------------- 좌표 변환 */

  toScreenX(x: Micron): number {
    return (x - this.center.x) * this.scale + this.width / 2;
  }

  toScreenY(y: Micron): number {
    // Y축 반전
    return this.height / 2 - (y - this.center.y) * this.scale;
  }

  toScreen(p: Pt): { x: number; y: number } {
    return { x: this.toScreenX(p.x), y: this.toScreenY(p.y) };
  }

  /** 화면 좌표 → 월드 좌표. 결과는 정수 µm 로 반올림된다. */
  toWorld(sx: number, sy: number): Pt {
    return pt(
      (sx - this.width / 2) / this.scale + this.center.x,
      (this.height / 2 - sy) / this.scale + this.center.y
    );
  }

  /** 화면 픽셀 → 월드 길이. 스냅 조리개·히트 조리개 계산에 쓴다. */
  pxToWorld(px: number): Micron {
    return Math.max(1, Math.round(px / this.scale));
  }

  /** 월드 길이 → 화면 픽셀 */
  worldToPx(v: Micron): number {
    return v * this.scale;
  }

  /* --------------------------------------------------------- 팬 / 줌 */

  /** 화면 픽셀만큼 화면을 민다 */
  panByPixels(dxPx: number, dyPx: number): void {
    this.center = pt(
      this.center.x - dxPx / this.scale,
      this.center.y + dyPx / this.scale // Y 반전
    );
  }

  /**
   * 커서 위치를 고정한 채 확대/축소.
   * 이 동작이 어긋나면 사용자는 즉시 "이상하다"고 느낀다.
   */
  zoomAt(sx: number, sy: number, factor: number): void {
    const before = this.toWorld(sx, sy);
    this.scale = clamp(this.scale * factor, MIN_SCALE, MAX_SCALE);
    const after = this.toWorld(sx, sy);
    // 같은 화면 지점이 같은 월드 좌표를 가리키도록 중심을 보정
    this.center = pt(
      this.center.x + (before.x - after.x),
      this.center.y + (before.y - after.y)
    );
  }

  /** 지정한 월드 상자가 화면에 꽉 차게 (ZOOM Window / Extents) */
  fit(box: Box, paddingPx = 40): void {
    const w = Math.max(1, box.maxX - box.minX);
    const h = Math.max(1, box.maxY - box.minY);
    const sx = (this.width - paddingPx * 2) / w;
    const sy = (this.height - paddingPx * 2) / h;
    this.scale = clamp(Math.min(sx, sy), MIN_SCALE, MAX_SCALE);
    this.center = boxCenter(box);
  }

  /** 현재 화면이 덮는 월드 영역. 뷰포트 컬링 질의에 쓴다. */
  visibleBox(marginPx = 0): Box {
    const a = this.toWorld(-marginPx, -marginPx);
    const b = this.toWorld(this.width + marginPx, this.height + marginPx);
    return {
      minX: Math.min(a.x, b.x),
      minY: Math.min(a.y, b.y),
      maxX: Math.max(a.x, b.x),
      maxY: Math.max(a.y, b.y),
    };
  }

  /**
   * 현재 배율에서 적당한 격자 간격(µm).
   * 화면상 최소 8px 이상 되는 1·2·5 계열 값을 고른다.
   */
  gridStep(minPx = 8): Micron {
    const targetUm = minPx / this.scale;
    const pow = Math.pow(10, Math.floor(Math.log10(targetUm)));
    for (const mult of [1, 2, 5, 10]) {
      const step = pow * mult;
      if (step >= targetUm) return Math.round(step);
    }
    return Math.round(pow * 10);
  }

  /** 화면 축척 표기 (1:100 등) — 상태바용. 96 DPI 가정. */
  scaleLabel(): string {
    const umPerScreenInch = 96 / this.scale;
    const ratio = umPerScreenInch / 25_400; // 1 inch = 25.4mm = 25,400µm
    if (ratio >= 1) return `1:${Math.round(ratio)}`;
    return `${Math.round(1 / ratio)}:1`;
  }

  snapshot(): { center: Pt; scale: number } {
    return { center: { ...this.center }, scale: this.scale };
  }

  restore(s: { center: Pt; scale: number }): void {
    this.center = { ...s.center };
    this.scale = clamp(s.scale, MIN_SCALE, MAX_SCALE);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
